import { DEFAULT_PAPER_PRESET_ID, rollPresetForLabelSize } from "./paperSizes";
import { PaperKind } from "./types";

// Everything about how a template is put on paper: the paper/stock, the
// custom size, the roll layout and the snapshot adjustments. A template has
// a default (stored with the template, for everyone) and each person can keep
// their own on top of it, since staff print on different printers.

export interface RollLayout {
  columns: number;
  rowsPerPage: number;
  leftMarginMm: number;
  rightMarginMm: number;
  middleGapMm: number;
  rowGapMm: number;
}

export interface PrintSetup {
  paperId: string;
  customKind: PaperKind;
  customPageId: string;
  customWidthMm: number;
  customHeightMm: number;
  pageFraction: { columns: number; rows: number } | null;
  roll: RollLayout;
  rotationDegrees: 0 | 90 | 180 | 270;
  zoomPercent: number;
  cropToFill: boolean;
}

// The shop's 50×30 roll: 2 labels across, the printer feeds 2 rows per page,
// 0.5mm liner on each side, 1mm between columns and between rows.
export const SHOP_50X30_ROLL_LAYOUT: RollLayout = {
  columns: 2,
  rowsPerPage: 2,
  leftMarginMm: 0.5,
  rightMarginMm: 0.5,
  middleGapMm: 1,
  rowGapMm: 1,
};

// Widest label a desktop thermal printer like the Zebra GC420d prints.
const ROLL_PRINT_WIDTH_MM = 104;
const ROLL_SIDE_MARGIN_MM = 0.5;
const ROLL_GAP_MM = 1;

// A label much taller than wide (the 50×150 long sticker) isn't a roll label
// here: it prints on ordinary paper, tiled on A4.
const isLongLabel = (widthMm: number, heightMm: number) => heightMm >= 2 * widthMm;

const rollLayoutFor = (widthMm: number): RollLayout => {
  const columns = Math.max(
    1,
    Math.floor(
      (ROLL_PRINT_WIDTH_MM - 2 * ROLL_SIDE_MARGIN_MM + ROLL_GAP_MM) / (widthMm + ROLL_GAP_MM),
    ),
  );

  return {
    columns,
    rowsPerPage: 1,
    leftMarginMm: ROLL_SIDE_MARGIN_MM,
    rightMarginMm: ROLL_SIDE_MARGIN_MM,
    middleGapMm: ROLL_GAP_MM,
    rowGapMm: ROLL_GAP_MM,
  };
};

const base = {
  customPageId: "a4",
  pageFraction: null,
  rotationDegrees: 0 as const,
  zoomPercent: 100,
  cropToFill: false,
};

/**
 * The setup a template gets when neither the person nor the template has one:
 * a thermal roll of the template's own size (the known 50×30 roll with the
 * shop's 2 × 2 layout), or A4 for long stickers and templates without a size.
 */
export const defaultPrintSetup = (
  widthMm?: number | null,
  heightMm?: number | null,
): PrintSetup => {
  if (!widthMm || !heightMm) {
    return {
      ...base,
      paperId: DEFAULT_PAPER_PRESET_ID,
      customKind: "sheet",
      customWidthMm: 50,
      customHeightMm: 30,
      roll: rollLayoutFor(50),
    };
  }

  if (isLongLabel(widthMm, heightMm)) {
    return {
      ...base,
      paperId: "custom",
      customKind: "sheet",
      customWidthMm: widthMm,
      customHeightMm: heightMm,
      roll: rollLayoutFor(widthMm),
    };
  }

  const preset = rollPresetForLabelSize(widthMm, heightMm);

  if (preset) {
    return {
      ...base,
      paperId: preset.id,
      customKind: "roll",
      customWidthMm: widthMm,
      customHeightMm: heightMm,
      roll: preset.id === "roll-50x30" ? SHOP_50X30_ROLL_LAYOUT : rollLayoutFor(widthMm),
    };
  }

  return {
    ...base,
    paperId: "custom",
    customKind: "roll",
    customWidthMm: widthMm,
    customHeightMm: heightMm,
    roll: rollLayoutFor(widthMm),
  };
};

const isNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value) && value >= 0;

/** A stored setup, or null when it's missing or malformed. */
export const parsePrintSetup = (raw: unknown): PrintSetup | null => {
  if (!raw || typeof raw !== "object") {
    return null;
  }

  const setup = raw as Partial<PrintSetup>;
  const roll = setup.roll as Partial<RollLayout> | undefined;

  if (
    typeof setup.paperId !== "string" ||
    (setup.customKind !== "sheet" && setup.customKind !== "roll") ||
    typeof setup.customPageId !== "string" ||
    !isNumber(setup.customWidthMm) ||
    !isNumber(setup.customHeightMm) ||
    !roll ||
    !isNumber(roll.columns) ||
    !isNumber(roll.rowsPerPage) ||
    !isNumber(roll.leftMarginMm) ||
    !isNumber(roll.rightMarginMm) ||
    !isNumber(roll.middleGapMm) ||
    !isNumber(roll.rowGapMm)
  ) {
    return null;
  }

  return {
    paperId: setup.paperId,
    customKind: setup.customKind,
    customPageId: setup.customPageId,
    customWidthMm: setup.customWidthMm,
    customHeightMm: setup.customHeightMm,
    pageFraction: setup.pageFraction ?? null,
    roll: roll as RollLayout,
    rotationDegrees: ([0, 90, 180, 270] as const).includes(
      setup.rotationDegrees as 0 | 90 | 180 | 270,
    )
      ? (setup.rotationDegrees as 0 | 90 | 180 | 270)
      : 0,
    zoomPercent: isNumber(setup.zoomPercent) ? setup.zoomPercent : 100,
    cropToFill: setup.cropToFill === true,
  };
};

// Each person's own setup per template ("auto" for the Automatic choice),
// kept in this browser: it describes their printer, not the template.
const userSetupKey = (userId: string | undefined, templateKey: string) =>
  `labelPrint.setup.${userId ?? "anonymous"}.${templateKey}`;

export const readUserPrintSetup = (
  userId: string | undefined,
  templateKey: string,
): PrintSetup | null => {
  try {
    return parsePrintSetup(
      JSON.parse(localStorage.getItem(userSetupKey(userId, templateKey)) ?? "null"),
    );
  } catch {
    return null;
  }
};

export const writeUserPrintSetup = (
  userId: string | undefined,
  templateKey: string,
  setup: PrintSetup | null,
) => {
  try {
    if (setup) {
      localStorage.setItem(userSetupKey(userId, templateKey), JSON.stringify(setup));
    } else {
      localStorage.removeItem(userSetupKey(userId, templateKey));
    }
  } catch {
    // Storage unavailable (private window): the setup just isn't remembered.
  }
};

/** Whose setup applies: the person's own, else the template's, else the default. */
export const resolvePrintSetup = (
  userSetup: PrintSetup | null,
  templateSetup: PrintSetup | null,
  widthMm?: number | null,
  heightMm?: number | null,
): PrintSetup => userSetup ?? templateSetup ?? defaultPrintSetup(widthMm, heightMm);
